<template>
  <div v-on-click-outside="handleClickOutside" :class="['gam-input']">
    <EmojiPicker
      v-if="props.isEmoji && isEmojiPickerVisible"
      class="emoji-picker"
      :native="true"
      :offset="0"
      :disabled-groups="['flags', 'symbols']"
      :additional-groups="{ chess: [{ n: ['chess pawn', 'pawn'], u: '265f-fe0f' }] }"
      :group-names="{ chess: 'Chess' }"
      :group-order="['chess']"
      @select="onSelectEmoji"
      theme="dark"
      hide-search
      disable-skin-tones
      hide-group-icons
      hide-group-names
    />

    <div :class="['gam-input-container', getContainerClasses, containerClass]">
      <div v-if="getLeftIcon" class="icon icon-left">
        <gam-icon
          v-bind="getLeftIcon"
          @mousedown="handleDecrement"
          @mouseup="mouseupEvent"
          @mouseleave="mouseupEvent"
        />
      </div>

      <div :class="['input-content', getContentClasses]">
        <div v-if="placeholder" class="placeholder">
          {{ localize(placeholder) }}
        </div>

        <div v-if="placeholder" class="label">
          {{ localize(placeholder) }}
        </div>

        <!-- <div v-if="flagIcon" class="input-flag">
          <gam-icon :name="flagIcon" :is-flag="true" />
        </div> -->

        <input
          ref="inputRef"
          v-bind="$attrs"
          v-model="inputValue"
          :name="name"
          class="input"
          :type="props.type"
          :readonly="readOnly"
          :maxlength="maxLength"
          :minlength="minLength"
          :placeholder="counter && value ? value?.toString() : undefined"
          :style="inputWidth"
          :disabled="isDisabled"
          :required="isRequired"
          :autocomplete="autocomplete"
          :autofocus="!!autofocus"
          @focus="setFocus"
          @blur="setBlur"
          @keydown="validateInput"
          @change="updateInput"
        />

        <div v-if="suffix" class="input-suffix">
          {{ suffix }}
        </div>
        <!-- </div> -->
      </div>

      <div v-if="getRightIcon" class="icon icon-right">
        <gam-icon
          v-bind="getRightIcon"
          @mousedown="handleIncrement"
          @mouseup="mouseupEvent"
          @mouseleave="mouseupEvent"
        />
      </div>

      <div v-if="isLoading" class="icon icon-right spin">
        <gam-icon class="load-icon" :size="GamIconSize.SMALL" :name="GamIconName.LOADING" :is-primary="true" />
      </div>

      <div v-if="inputValue && canClear" class="icon icon-close icon-right">
        <div class="right-fade" />
        <gam-icon :name="GamIconName.X_CLOSE" :size="GamIconSize.LARGE" @click="clearInput" />
      </div>

      <div v-else-if="props.isSearch && props.onClickClear && inputValue" class="icon icon-close icon-right">
        <div class="right-fade" />
        <gam-icon :name="GamIconName.X_CLOSE" :size="GamIconSize.LARGE" @click="() => handleClear()" />
      </div>
    </div>

    <transition name="dropdown" mode="out-in">
      <gam-button-message v-if="message" class="error-message" v-bind="message" @click="onClickMessage" />
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { localize } from '@/core/gambits';
import GamButtonMessage from '@/views/components/GamButtonMessage.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import {
  GamIconName,
  GamIconSize,
  GamIconSpecialName,
} from '@/views/composables/constants/components/gamIcon.constants';
import { GamInputName, GamInputValueType } from '@/views/composables/constants/components/gamInput.constants';
import { Key } from '@/views/composables/constants/gam.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import type { GamIconType } from '@/views/composables/models/components/GamIcon';
import type { GamInputType } from '@/views/composables/models/components/GamInput';
import { vOnClickOutside } from '@vueuse/components';
import { computed, nextTick, onMounted, ref, watch, type StyleValue } from 'vue';
import EmojiPicker from 'vue3-emoji-picker';

import 'vue3-emoji-picker/css';

const props = withDefaults(defineProps<GamInputType>(), {
  type: GamInputValueType.TEXT,
  autocomplete: 'off',
});

const emit = defineEmits([
  GamComponentsEmits.UPDATE_VALUE,
  GamComponentsEmits.UPDATE,
  GamComponentsEmits.CLEAR,
  GamComponentsEmits.CLOSE,
]);

const lockInput = ref<boolean>(false);

const hasModelValue = computed(() => props.modelValue !== undefined);
const externalValue = computed(() => (hasModelValue.value ? props.modelValue : props.value));

const setInputValue = (): string | number | null | undefined => {
  let value: string | number | undefined = 0;
  if (!props.counter) {
    if (props.type === GamInputValueType.NUMBER) {
      value = (externalValue.value as string | number | undefined) || undefined;
    }
  }
  return value || externalValue.value;
};

const inputRef = ref<HTMLInputElement>();
const inputValue = ref<string | number | null | undefined>(setInputValue());
const isActive = ref<boolean>(false);
const operation = ref<number>(0);
const incrementInterval = ref<NodeJS.Timeout>();

const getIconValues = (name: GamIconName | GamIconSpecialName): GamIconType => {
  return {
    name,
    size: props.isSearch ? GamIconSize.X_LARGE : GamIconSize.NORMAL,
    isCurrent: props.name === GamInputName.COUNTER,
    isPrimary: props.name !== GamInputName.COUNTER && (props.isPrimary || props.isHighlit),
  };
};

const setFocus = (e: Event): void => {
  if (props.isEmoji) {
    e.preventDefault();
    e.stopPropagation();
    if (isEmojiPickerVisible.value === false) {
      showEmojiPicker();
    } else {
      hideEmojiPicker();
    }
    inputRef.value?.blur();
    return;
  }

  props.onFocus?.();
  setTimeout(() => {
    if (props.readOnly) return;
    isActive.value = true;
  });
};

const setBlur = (): void => {
  if (props.isEmoji) {
    return;
  }

  if (props.isDropdownOpen) return;

  inputValue.value = setInputValue();

  inputRef.value?.blur();

  props.onBlur?.();
  setTimeout(() => {
    // emit(GamComponentsEmits.CLOSE);
    isActive.value = false;
  }, 150);
};

const mouseupEvent = () => {
  if (props.counter) {
    clearInterval(incrementInterval?.value);
  }
};

const handleIncrement = (e: Event) => {
  e.preventDefault();

  mousedownEvent(1);
};

const handleDecrement = (e: Event) => {
  e.preventDefault();

  mousedownEvent(-1);
};

const mousedownEvent = (opr: number) => {
  if (!props.counter) {
    return;
  }

  operation.value = opr;

  const step: number = props.counter?.step || 1;
  const fallbackValue = externalValue.value;
  const fallbackNumber =
    typeof fallbackValue === 'number' ? fallbackValue : parseInt((fallbackValue ?? '') as string, 10);
  const currentValue: number =
    inputValue.value || inputValue.value === 0 ? parseInt(inputValue.value as string, 10) : fallbackNumber || 0;

  setCounterValue(currentValue + opr * step);
};

// TODO: Extract counter logic into GamCounter

const setCounterValue = (change: number): void => {
  isActive.value = true;

  if (props?.counter?.max && change >= props?.counter?.max) {
    return;
  }

  if (props.counter?.min && change < props?.counter?.min) {
    return;
  }

  emit(GamComponentsEmits.UPDATE_VALUE, change);
};

const checkSelectAll = (event: KeyboardEvent) => {
  return (event.ctrlKey || event.metaKey) && event.key === 'a';
};

const validateInput = (event: KeyboardEvent) => {
  if (props.isUsername && event.key.toLocaleLowerCase().replace(/[^a-z0-9_\.]/g, '').length === 0) {
    // event.stopPropagation();
    event.preventDefault();
    return;
  }

  if (checkSelectAll(event)) return;

  if (!lockInput.value || event.key === Key.BACKSPACE) {
    lockInput.value = false;

    if (!isNumber()) return;

    if (!isFinite(parseInt(event.key, 10))) {
      if (event.key?.length === 1) {
        // event.stopPropagation();
        event.preventDefault();
      } else {
        return;
      }
    }
  } else {
    // event.stopPropagation();
    event.preventDefault();
  }
};

const isNumber = () => {
  return props.type === GamInputValueType.NUMBER;
};

const updateInput = (event: Event) => {
  const target = event?.target as HTMLInputElement;
  if (!target.value) return;

  let newValue = target.value;

  if (isNumber()) {
    newValue = String(parseInt(target.value.toString(), 10));
  }

  emit(GamComponentsEmits.UPDATE_VALUE, newValue || '');
};

const clearInput = () => {
  props.onClickClear && props.onClickClear(inputValue);
  emit(GamComponentsEmits.UPDATE, '');
  emit(GamComponentsEmits.UPDATE_VALUE, '');
  emit(GamComponentsEmits.CLEAR);
};

const isEmojiPickerVisible = ref<boolean>(false);
const showEmojiPicker = () => {
  isEmojiPickerVisible.value = true;
};
const hideEmojiPicker = () => {
  isEmojiPickerVisible.value = false;
};
const handleClickOutside = () => {
  if (props.isEmoji && isEmojiPickerVisible.value) {
    hideEmojiPicker();
  }
};
const onSelectEmoji = (emoji: any) => {
  emit(GamComponentsEmits.UPDATE, emoji.i);
  emit(GamComponentsEmits.UPDATE_VALUE, emoji.i);
  nextTick(() => {
    hideEmojiPicker();
  });
};

const handleClear = () => {
  clearInput();
  props.onClickClear?.(inputValue);
};

const isInputActive = (): boolean => {
  const valueEntered = inputValue.value?.toString();
  return isActive.value || !!valueEntered?.length;
};

const getContainerClasses = computed(() => ({
  'is-active': isActive.value,
  'is-disabled': props.isDisabled || props.isLoading,
  'is-centered': props.isCentered,
  'is-error': props.isError,
  'is-highlit': props.isHighlit,
  'read-only': props.readOnly,
  'is-emoji': props.isEmoji,
  'has-left-icon': !!props.leftIcon && !props.rightIcon && !props.canClear,
  'has-right-icon': (!!props.rightIcon || !!props.canClear) && !props.leftIcon,
  'has-both-icons': !!props.leftIcon && (!!props.rightIcon || !!props.canClear),
  counter: props.counter,
}));

const getContentClasses = computed(() => ({
  active: isInputActive(),
}));

const getLeftIcon = computed((): GamIconType | undefined => {
  if (props.leftIcon && !(props.counter && props.readOnly)) {
    return getIconValues(props.leftIcon);
  } else if (props.counter) {
    return { name: props.leftIcon!, isCurrent: true };
  }

  return undefined;
});

const getRightIcon = computed((): GamIconType | undefined => {
  if (props.rightIcon && !(props.counter && props.readOnly)) {
    return getIconValues(props.rightIcon);
  } else if (props.counter) {
    return { name: props.rightIcon!, isCurrent: true };
  }
  return undefined;
});

const inputWidth = computed((): StyleValue => {
  const value = inputValue.value?.toString();
  return {
    width: props.suffix ? `${(value?.length || 0) * 9}px` : 'inherit',
  };
});

watch(inputValue, (value, oldValue) => {
  if (props.readOnly) return;
  lockInput.value = !!(value?.toString().length && props.isEmoji);

  let newValue = value;

  if (props.isLowerCase) {
    newValue = String(newValue).toLocaleLowerCase();
  }

  if (isNumber() && !props.counter) {
    const maxValue = props.max ? parseInt(props.max.toString(), 10) : Math.max();
    const previewValue = parseInt(newValue?.toString() || '', 10);

    if (previewValue > maxValue) {
      inputValue.value = oldValue;
      newValue = oldValue;
    }
  }
  newValue = newValue && isNumber() ? parseInt(newValue.toString(), 10) : newValue;

  emit(GamComponentsEmits.UPDATE, newValue);
  emit(GamComponentsEmits.UPDATE_VALUE, newValue || '');
});

watch(externalValue, () => {
  const value = externalValue.value;
  if (value || value === 0) {
    inputValue.value = setInputValue();
  } else {
    inputValue.value = value;
  }
});

watch(
  () => props.isLoading,
  () => {
    lockInput.value = props.isLoading;
  },
);

defineExpose({
  isActive,
  setFocus,
  setBlur,
});

onMounted(() => {
  props.onMounted && inputRef.value && props.onMounted(inputRef.value);
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

.gam-input {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  flex-direction: column;
  gap: 12px;
  position: relative;

  .error-message {
    position: absolute;
    top: 52px;
  }

  .gam-input-container {
    width: inherit;
    border-radius: var(--radius-large);
    height: 47px !important;
    cursor: text;
    border: 1px solid var(--color-white-20);
    transition: border var(--transition);
    position: relative;
    @extend %gam-special-border;
    background-color: var(--color-dark-20);
    -webkit-backdrop-filter: blur(15px);
    backdrop-filter: blur(15px);

    &:before {
      background: var(--color-linear-gradient);
      opacity: 0;
      inset: -1px;
    }

    @include hover {
      &:before {
        opacity: 1;
      }
    }

    .input-content {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
    }

    &.is-emoji input {
      line-height: 1;
    }

    &.has-left-icon,
    &.has-both-icons {
      .input-content {
        left: 18px;
      }
    }

    &.has-right-icon,
    &.has-both-icons {
      .input-content {
        right: 18px;
      }
    }

    .input-content {
      transition: inherit;
      &.active {
        .label {
          opacity: 1;
        }

        .input-area {
          .input-suffix {
            display: block;
          }
        }

        .placeholder {
          opacity: 0;
        }
      }

      .label {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.15em;
        text-transform: uppercase;
        color: var(--color-white-30);
        transition:
          opacity var(--transition-fast),
          top var(--transition-fast);
        transition-delay: 128ms;
        position: absolute;
        opacity: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: pre;
        top: 5px;
        left: 22px;
        pointer-events: none;
      }

      .placeholder {
        color: var(--color-white-50);
        transition:
          opacity var(--transition-fast),
          margin-bottom var(--transition-fast);
        transition-delay: 128ms;
        position: absolute;
        opacity: 1;
        top: 50%;
        transform: translateY(-50%);
        left: calc(var(--spacing-l) + var(--spacing-s));
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: pre;
        pointer-events: none;
      }

      .input {
        font-size: 16px;
        font-weight: 300;
        color: var(--color-white-100);
        padding: 20px calc(var(--spacing-l) + var(--spacing-s)) 8px;
        position: absolute;
        align-self: center;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        box-sizing: border-box;
        pointer-events: all;
      }

      .input-suffix {
        display: none;
        align-self: center;
        min-width: max-content;
      }

      .input-flag {
        width: max-content;
        height: 16px;
      }
    }

    .icon {
      display: block;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;

      x &.icon-close {
        cursor: pointer;
      }
    }

    .icon-left,
    .icon-right {
      z-index: 9;
    }

    .input-content {
      z-index: 8;
    }

    .icon-left {
      position: absolute;
      left: 5px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      align-items: center;
      justify-content: center;

      span {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      span,
      svg {
        width: 32px;
        height: 32px;
      }
    }

    .icon-right {
      position: absolute;
      right: 5px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: auto;
      cursor: pointer;
    }

    &.is-active,
    &.is-highlit {
      &:before {
        opacity: 1;
      }

      .input-content {
        .input {
          &::placeholder {
            display: none;
            color: transparent;
          }
        }
      }
    }

    &.is-disabled {
      cursor: default;
      -webkit-user-select: none;
      user-select: none;
      pointer-events: none;
      opacity: 0.3;

      &:before {
        content: none;
      }
    }

    &.is-error {
      border-color: var(--color-red) !important;

      &:before {
        content: none;
      }
    }

    &.counter {
      padding: var(--spacing-l);

      .input-area {
        justify-content: center;
      }

      .input {
        text-align: center;
        padding-top: 5px;

        &::placeholder {
          color: var(--color-white-50);
        }
      }

      .icon {
        cursor: pointer;
      }

      .icon {
        color: var(--color-white);
      }
      @include hover-capable {
        .icon:hover {
          color: var(--color-pink);
        }
      }

      &.is-active,
      &.is-highlit {
        .input {
          color: var(--color-white-100);
        }
      }
    }

    &.read-only {
      cursor: default;

      .label,
      .placeholder {
        transition: none !important;
      }

      .input {
        color: var(--color-white-100);
        transition: none !important;
      }
    }
  }
}

.gam-input .gam-input-container.is-highlit .input-content .placeholder {
  color: var(--color-white);
  animation: attention 1000ms ease-in-out infinite alternate;

  @keyframes attention {
    0% {
      color: var(--color-highlight);
    }

    100% {
      color: var(--color-white);
    }
  }
}

.emoji-picker {
  position: absolute;
  z-index: 2;
  top: 56px;
  right: 0;
  // transform: translate(-50%, -50%);
}
</style>
